import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAgentLead,
  saveAgentLead,
  saveAgentLeadKeepalive,
  type LeadFieldSpec,
  type LeadSaveBody,
  type LeadValues,
} from "../api/leads";

/**
 * Lead capture (F6) — the form a rep types into while talking to the person.
 *
 * The lists this floor dials carry a phone number and nothing else, so the rep
 * is the data source and, until now, what they learned died with the call.
 * Three rules follow, and every line of state below serves one of them:
 *
 *  1. It never gets in the way of the call. Nothing is required, nothing
 *     blocks a transfer or a disposition, and there is no Save button to
 *     remember — it saves on every pause in typing, on blur, and when the
 *     call ends.
 *  2. Typed text is never lost, even if the customer hangs up mid-word or the
 *     tab is closed. Every keystroke goes to localStorage first (`writeDraft`);
 *     the draft is cleared only once the server has acknowledged a save that
 *     includes it, and `flushLeadDrafts` replays any leftover draft the next
 *     time a console mounts. `pagehide` fires a keepalive save on top.
 *     And nothing the SERVER sends ever replaces a keystroke: the form pops
 *     while the call is still ringing and the rep is told to type, so the
 *     saved copy / a colleague's pre-fill may well arrive after they have
 *     started (see the seed effect).
 *  3. A colleague's earlier notes on the same number are offered as a
 *     pre-fill, clearly labelled, and only written back if the rep edits.
 *
 * Drafts are scoped (`draftScope`) to WHO is typing on WHICH seat. Floor PCs
 * are shared and a campaign re-dials a lead under the same spool_id, so an
 * unscoped draft stranded on Monday would surface in — and be replayed by —
 * whichever rep next opened a console on that machine, over whatever a later
 * rep had saved.
 */

export const DRAFT_PREFIX = "lead-draft:";

export interface LeadDraft {
  /** Who typed this, on which seat — see `draftScope` in AgentPanelPage. */
  scope: string;
  spool_id: number;
  /** The seat the form was open on; sent back so the server can bind the row. */
  extension_id: number | null;
  first_name: string;
  last_name: string;
  fields: LeadValues;
  at: number;
}

// Autosave waits this long after the last keystroke. Short enough that a
// hangup two seconds after typing a name has already been saved; long enough
// not to write on every character.
export const AUTOSAVE_MS = 900;
const RETRY_MS = 5000;

function draftKey(scope: string, spoolId: number): string {
  return `${DRAFT_PREFIX}${scope}:${spoolId}`;
}

// The first release keyed drafts by spool_id alone (`lead-draft:<digits>`).
// Those cannot be attributed to a rep or a seat, so they are never replayed —
// and are removed rather than left to accumulate.
const LEGACY_KEY = /^lead-draft:\d+$/;

function parseDraft(raw: string | null, scope: string, spoolId: number): LeadDraft | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const d = parsed as Partial<LeadDraft>;
    if (d.spool_id !== spoolId || d.scope !== scope) return null;
    return {
      scope,
      spool_id: spoolId,
      extension_id: typeof d.extension_id === "number" ? d.extension_id : null,
      first_name: typeof d.first_name === "string" ? d.first_name : "",
      last_name: typeof d.last_name === "string" ? d.last_name : "",
      fields: d.fields && typeof d.fields === "object" ? (d.fields as LeadValues) : {},
      at: typeof d.at === "number" ? d.at : 0,
    };
  } catch {
    return null;
  }
}

export function readDraft(scope: string, spoolId: number): LeadDraft | null {
  try {
    return parseDraft(window.localStorage.getItem(draftKey(scope, spoolId)), scope, spoolId);
  } catch {
    return null;
  }
}

export function writeDraft(draft: LeadDraft): void {
  try {
    window.localStorage.setItem(draftKey(draft.scope, draft.spool_id), JSON.stringify(draft));
  } catch {
    // Storage unavailable (private mode, quota) — the in-memory state and the
    // autosave still run; only the crash backstop is missing.
  }
}

export function clearDraft(scope: string, spoolId: number): void {
  try {
    window.localStorage.removeItem(draftKey(scope, spoolId));
  } catch {
    // see writeDraft
  }
}

/** Every draft left behind by THIS scope, and nobody else's. */
export function listDrafts(scope: string): LeadDraft[] {
  const out: LeadDraft[] = [];
  const prefix = `${DRAFT_PREFIX}${scope}:`;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const id = Number(key.slice(prefix.length));
      if (!Number.isInteger(id)) continue;
      const d = parseDraft(window.localStorage.getItem(key), scope, id);
      if (d) out.push(d);
    }
  } catch {
    // see writeDraft
  }
  return out;
}

function purgeLegacyDrafts(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && LEGACY_KEY.test(key)) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // see writeDraft
  }
}

function toBody(d: {
  spool_id: number;
  extension_id: number | null;
  first_name: string;
  last_name: string;
  fields: LeadValues;
}): LeadSaveBody {
  return {
    spool_id: d.spool_id,
    extension_id: d.extension_id,
    first_name: d.first_name.trim() || null,
    last_name: d.last_name.trim() || null,
    fields: d.fields,
  };
}

/**
 * Replay every draft the last session left behind FOR THIS SCOPE. Called once
 * when the agent panel knows who is on which seat. A draft the server refuses
 * outright (404: the call never popped on this seat, or has since been rung
 * on another one; 422) is dropped — retrying it forever would only
 * accumulate; anything else (network, 5xx) is kept for next time.
 */
export async function flushLeadDrafts(
  scope: string,
  save: (b: LeadSaveBody) => Promise<unknown> = saveAgentLead,
): Promise<number> {
  purgeLegacyDrafts();
  let flushed = 0;
  for (const draft of listDrafts(scope)) {
    try {
      await save(toBody(draft));
      clearDraft(scope, draft.spool_id);
      flushed++;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 422) clearDraft(scope, draft.spool_id);
    }
  }
  return flushed;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export interface LeadCapturePanelProps {
  spoolId: number;
  phone: string | null;
  /** The seat this call is on. Sent with every request; the server binds the row to it. */
  extensionId: number | null;
  /** Who is typing, on which seat — keys the on-device drafts. */
  draftScope: string;
  /** True once the call has ended and the rep is wrapping up. Only changes copy. */
  ended?: boolean;
}

export function LeadCapturePanel({ spoolId, phone, extensionId, draftScope, ended = false }: LeadCapturePanelProps) {
  const bundle = useQuery({
    queryKey: ["agent-lead", spoolId, extensionId],
    queryFn: () => getAgentLead(spoolId, extensionId),
    // The form definition and the saved capture do not change under the rep
    // during a call; refetching would only risk clobbering what they typed.
    // (A late answer can still arrive after typing has begun — the seed
    // effect below is what makes that harmless.)
    staleTime: Infinity,
    retry: 1,
  });

  // Draft first: it is the most recent thing the rep typed, whatever the
  // server has. Read once, synchronously, so a reload mid-call shows the
  // text immediately rather than after the request.
  const initialDraft = useMemo(() => readDraft(draftScope, spoolId), [draftScope, spoolId]);
  const [firstName, setFirstName] = useState(initialDraft?.first_name ?? "");
  const [lastName, setLastName] = useState(initialDraft?.last_name ?? "");
  const [fields, setFields] = useState<LeadValues>(initialDraft?.fields ?? {});
  const [prefilledFrom, setPrefilledFrom] = useState<{ agent: string | null; at: string | null } | null>(null);
  const seededRef = useRef(initialDraft !== null);

  // --- autosave machinery ------------------------------------------------
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Bumped on every edit; a save that started at version N clears the draft
  // only if nothing was typed in the meantime. Also the answer to "has the
  // rep typed anything at all since this form mounted" (> 0).
  const versionRef = useRef(0);
  const savedVersionRef = useRef(0);
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const latestRef = useRef({ spoolId, extensionId, firstName, lastName, fields });
  latestRef.current = { spoolId, extensionId, firstName, lastName, fields };
  const mountedRef = useRef(true);

  const doSave = useCallback(async () => {
    if (inFlightRef.current) return;
    const snapshot = latestRef.current;
    const version = versionRef.current;
    inFlightRef.current = true;
    if (mountedRef.current) setStatus("saving");
    try {
      await saveAgentLead(
        toBody({
          spool_id: snapshot.spoolId,
          extension_id: snapshot.extensionId,
          first_name: snapshot.firstName,
          last_name: snapshot.lastName,
          fields: snapshot.fields,
        }),
      );
      savedVersionRef.current = Math.max(savedVersionRef.current, version);
      if (versionRef.current === version) {
        clearDraft(draftScope, snapshot.spoolId);
        dirtyRef.current = false;
      }
      if (mountedRef.current) {
        setStatus("saved");
        setSavedAt(new Date());
      }
    } catch {
      // Keep the draft; say so; try again shortly. Never surface this as a
      // blocker — the rep is mid-conversation.
      if (mountedRef.current) {
        setStatus("error");
        timerRef.current = setTimeout(() => void doSave(), RETRY_MS);
      }
    } finally {
      inFlightRef.current = false;
      // Edits landed while the request was out: save them too.
      if (mountedRef.current && versionRef.current !== version && dirtyRef.current) {
        timerRef.current = setTimeout(() => void doSave(), AUTOSAVE_MS);
      }
    }
  }, [draftScope]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void doSave(), AUTOSAVE_MS);
  }, [doSave]);

  const flushNow = useCallback(() => {
    if (!dirtyRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    void doSave();
  }, [doSave]);

  const markEdited = useCallback(
    (next: { firstName: string; lastName: string; fields: LeadValues }) => {
      versionRef.current += 1;
      dirtyRef.current = true;
      // Editing a colleague's pre-fill makes it this call's own record.
      setPrefilledFrom(null);
      writeDraft({
        scope: draftScope,
        spool_id: spoolId,
        extension_id: extensionId,
        first_name: next.firstName,
        last_name: next.lastName,
        fields: next.fields,
        at: Date.now(),
      });
      scheduleSave();
    },
    [draftScope, spoolId, extensionId, scheduleSave],
  );

  // Once the server answers: a saved capture wins over NOTHING; a colleague's
  // earlier notes on this number are offered as a pre-fill. Neither may ever
  // replace a keystroke. The form pops while the call is still ringing and
  // the rep is told to type, so by the time this answer lands — slow network,
  // a retried GET, a refetch on window focus after a failed one — they may
  // already have. The test is "have they typed since mount" (`versionRef`),
  // NOT `dirtyRef`: a save may already have landed (dirty = false) while a
  // GET issued before that save still carries the OLDER copy.
  useEffect(() => {
    if (seededRef.current || !bundle.data) return;
    seededRef.current = true;
    const { capture, previous } = bundle.data;
    const typed = versionRef.current > 0;
    if (capture) {
      if (!typed) {
        setFirstName(capture.first_name ?? "");
        setLastName(capture.last_name ?? "");
        setFields(capture.fields ?? {});
        return;
      }
      // This row's own saved answers arrived after the rep started typing.
      // Their keystrokes win, field by field; what they have not touched
      // shows what was saved — and is then saved along WITH their text,
      // because the next PUT replaces the row whole and must not silently
      // drop answers they never saw.
      const cur = latestRef.current;
      const merged = {
        firstName: cur.firstName || (capture.first_name ?? ""),
        lastName: cur.lastName || (capture.last_name ?? ""),
        fields: { ...(capture.fields ?? {}), ...cur.fields },
      };
      setFirstName(merged.firstName);
      setLastName(merged.lastName);
      setFields(merged.fields);
      versionRef.current += 1;
      dirtyRef.current = true;
      writeDraft({
        scope: draftScope, spool_id: spoolId, extension_id: extensionId,
        first_name: merged.firstName, last_name: merged.lastName, fields: merged.fields,
        at: Date.now(),
      });
      scheduleSave();
      return;
    }
    if (previous && !typed) {
      setFirstName(previous.first_name ?? "");
      setLastName(previous.last_name ?? "");
      setFields(previous.fields ?? {});
      setPrefilledFrom({ agent: previous.agent_name, at: previous.at });
    }
    // previous && typed: the rep has begun their own record of this call. A
    // colleague's notes on this NUMBER — possibly about a different person —
    // are not mixed into it under their name.
  }, [bundle.data, draftScope, spoolId, extensionId, scheduleSave]);

  // A call that pops with a draft already on disk (the tab was reloaded
  // mid-call, or the last save never landed) is saved as soon as it mounts.
  useEffect(() => {
    if (initialDraft) {
      dirtyRef.current = true;
      versionRef.current += 1;
      scheduleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spoolId]);

  // Unmount (the rep dispositioned, or the next call replaced this one):
  // fire whatever is pending. Not awaited — the draft stays on disk until the
  // response, and flushLeadDrafts replays it if this never lands.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (dirtyRef.current && !inFlightRef.current) {
        const s = latestRef.current;
        void saveAgentLead(
          toBody({
            spool_id: s.spoolId, extension_id: s.extensionId,
            first_name: s.firstName, last_name: s.lastName, fields: s.fields,
          }),
        )
          .then(() => clearDraft(draftScope, s.spoolId))
          .catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The tab going away mid-call: a keepalive PUT survives page unload.
  useEffect(() => {
    const onHide = () => {
      if (!dirtyRef.current) return;
      const s = latestRef.current;
      saveAgentLeadKeepalive(
        toBody({
          spool_id: s.spoolId, extension_id: s.extensionId,
          first_name: s.firstName, last_name: s.lastName, fields: s.fields,
        }),
      );
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, []);

  // --- handlers ------------------------------------------------------------
  const onFirst = (v: string) => {
    setFirstName(v);
    markEdited({ firstName: v, lastName, fields });
  };
  const onLast = (v: string) => {
    setLastName(v);
    markEdited({ firstName, lastName: v, fields });
  };
  const onField = (key: string, v: string | number | boolean | null) => {
    const next = { ...fields };
    if (v === null || v === "") delete next[key];
    else next[key] = v;
    setFields(next);
    markEdited({ firstName, lastName, fields: next });
  };

  const form: LeadFieldSpec[] = bundle.data?.form ?? [];
  const loadError = bundle.isError;

  const statusText =
    status === "saving"
      ? "Saving…"
      : status === "error"
        ? "Not saved yet — kept on this device, retrying"
        : status === "saved"
          ? `Saved${savedAt ? ` ${savedAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}`
          : "Saves as you type";

  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand";

  return (
    <section
      aria-label="Lead details"
      data-testid="lead-capture"
      className="mb-4 overflow-hidden rounded-2xl bg-white shadow"
    >
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-6 py-3">
        <div>
          <div className="text-xs font-semibold uppercase text-gray-400">
            {ended ? "Lead details — finish up before you wrap up" : "Lead details"}
          </div>
          {phone && <div className="text-xs text-gray-400">{phone}</div>}
        </div>
        <span
          role="status"
          aria-live="polite"
          className={`text-xs ${
            status === "error" ? "font-medium text-amber-700" : status === "saved" ? "text-emerald-700" : "text-gray-400"
          }`}
        >
          {statusText}
        </span>
      </div>

      <div className="px-6 py-4">
        {prefilledFrom && (
          <p className="mb-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900">
            Filled in from a previous call
            {prefilledFrom.agent ? ` by ${prefilledFrom.agent}` : ""}
            {prefilledFrom.at ? ` on ${formatWhen(prefilledFrom.at)}` : ""}. Edit anything that has changed.
          </p>
        )}
        {loadError && (
          <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" role="alert">
            Couldn't load this campaign's questions — the name still saves.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="lead-first-name" className="mb-1 block text-xs text-gray-500">
              First name
            </label>
            <input
              id="lead-first-name"
              className={inputClass}
              value={firstName}
              autoComplete="off"
              onChange={(e) => onFirst(e.target.value)}
              onBlur={flushNow}
            />
          </div>
          <div>
            <label htmlFor="lead-last-name" className="mb-1 block text-xs text-gray-500">
              Last name
            </label>
            <input
              id="lead-last-name"
              className={inputClass}
              value={lastName}
              autoComplete="off"
              onChange={(e) => onLast(e.target.value)}
              onBlur={flushNow}
            />
          </div>

          {form.map((f) => {
            const id = `lead-${f.key}`;
            const raw = fields[f.key];
            const wide = f.type === "textarea";
            const label = (
              <label htmlFor={id} className="mb-1 block text-xs text-gray-500">
                {f.label}
                {f.required && <span className="text-gray-400"> (ask)</span>}
              </label>
            );
            let control: JSX.Element;
            if (f.type === "textarea") {
              control = (
                <textarea
                  id={id}
                  rows={2}
                  className={inputClass}
                  value={raw === undefined ? "" : String(raw)}
                  placeholder={f.placeholder ?? undefined}
                  onChange={(e) => onField(f.key, e.target.value)}
                  onBlur={flushNow}
                />
              );
            } else if (f.type === "select") {
              control = (
                <select
                  id={id}
                  className={inputClass}
                  value={raw === undefined ? "" : String(raw)}
                  onChange={(e) => onField(f.key, e.target.value)}
                  onBlur={flushNow}
                >
                  <option value="">—</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                  {raw !== undefined && !f.options.includes(String(raw)) && (
                    <option value={String(raw)}>{String(raw)}</option>
                  )}
                </select>
              );
            } else if (f.type === "boolean") {
              control = (
                <select
                  id={id}
                  className={inputClass}
                  value={raw === true ? "yes" : raw === false ? "no" : ""}
                  onChange={(e) =>
                    onField(f.key, e.target.value === "yes" ? true : e.target.value === "no" ? false : null)
                  }
                  onBlur={flushNow}
                >
                  <option value="">—</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              );
            } else {
              const inputType =
                f.type === "number" ? "text" : f.type === "date" ? "date" : f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text";
              control = (
                <input
                  id={id}
                  type={inputType}
                  inputMode={f.type === "number" ? "decimal" : undefined}
                  className={inputClass}
                  value={raw === undefined ? "" : String(raw)}
                  placeholder={f.placeholder ?? undefined}
                  autoComplete="off"
                  onChange={(e) => onField(f.key, e.target.value)}
                  onBlur={flushNow}
                />
              );
            }
            return (
              <div key={f.key} className={wide ? "col-span-2" : ""}>
                {label}
                {control}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
